import { AUTO_PHONE_VERIFICATION_ORGANIZATION_ID } from '@/constants';

/** Base JWT claims we might care about */
export interface JwtBaseClaims {
  exp?: number; // seconds since epoch
  iat?: number; // seconds since epoch
  nbf?: number; // seconds since epoch
}

/** Decoded JWT container */
export interface DecodedJWT<T extends Record<string, unknown> = Record<string, unknown>> {
  header: Record<string, unknown>;
  payload: T & JwtBaseClaims;
  signature: string;
}

const JWT_PARTS_COUNT = 3;
const BASE64_PAD_UNIT = 4;
const MS_TO_SEC = 1000;

/** Base64-url decode (browser) */
function base64UrlDecode(str: string): string {
  const base64 = str.replace(/-/g, '+').replace(/_/g, '/');
  const padLength = (BASE64_PAD_UNIT - (base64.length % BASE64_PAD_UNIT)) % BASE64_PAD_UNIT;
  const padded = base64.padEnd(base64.length + padLength, '=');
  try {
    return atob(padded);
  } catch {
    throw new Error('Invalid base64 string in JWT');
  }
}

/** Safely ensure a value is a plain object with string keys. */
function asRecord(val: unknown): Record<string, unknown> {
  if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(val)) {
      result[key] = value;
    }
    return result;
  }
  return {};
}
/**
 * Pick only JWT base claims from a payload object.
 */
function pickJwtBaseClaims(obj: Record<string, unknown>): JwtBaseClaims {
  const claims: JwtBaseClaims = {};
  if (typeof obj.exp === 'number') claims.exp = obj.exp;
  if (typeof obj.iat === 'number') claims.iat = obj.iat;
  if (typeof obj.nbf === 'number') claims.nbf = obj.nbf;
  return claims;
}

/**
 * Decode a JWT (no signature verification).
 * @param token JWT string
 * @returns Decoded header, payload, signature
 */
export function decodeJWT(token: string): DecodedJWT {
  if (typeof token !== 'string' || token === '') {
    throw new Error('Invalid token provided');
  }

  const parts: string[] = token.split('.');
  if (parts.length !== JWT_PARTS_COUNT) {
    throw new Error('Invalid JWT format');
  }

  const [headerB64, payloadB64, signature] = parts;

  let headerJson: unknown = {};
  let payloadJson: unknown = {};

  try {
    headerJson = JSON.parse(base64UrlDecode(headerB64));
  } catch (err) {
    const message: string = err instanceof Error ? err.message : String(err);
    throw new Error(`Failed to decode JWT header: ${message}`);
  }

  try {
    payloadJson = JSON.parse(base64UrlDecode(payloadB64));
  } catch (err) {
    const message: string = err instanceof Error ? err.message : String(err);
    throw new Error(`Failed to decode JWT payload: ${message}`);
  }

  const header: Record<string, unknown> = asRecord(headerJson);
  const payloadObj: Record<string, unknown> = asRecord(payloadJson);

  // Compose payload with explicit claim picking and spread for generic claims
  const payload: Record<string, unknown> & JwtBaseClaims = {
    ...payloadObj,
    ...pickJwtBaseClaims(payloadObj),
  };

  return {
    header,
    payload,
    signature,
  };
}
/**
 * Check if a JWT is expired (uses `exp` claim if present).
 * Returns true on invalid tokens as a safe default.
 */
export function isJWTExpired(token: string): boolean {
  try {
    const { payload } = decodeJWT(token);
    if (typeof payload.exp !== 'number') {
      return false;
    }
    const nowSec: number = Math.floor(Date.now() / MS_TO_SEC);
    return payload.exp < nowSec;
  } catch {
    return true;
  }
}

/**
 * Get a specific claim (typed) from a JWT payload.
 * Returns undefined if token is invalid or claim missing.
 */
export function getJwtClaim(token: string, claim: string): unknown {
  try {
    const { payload } = decodeJWT(token);
    return (payload as Record<string, unknown>)[claim];
  } catch {
    return undefined;
  }
}

/**
 * Extract user ID from JWT token's 'sub' claim
 * @param token JWT token string
 * @returns User ID string or null if invalid/missing
 */
export function getUserIdFromToken(token: string): string | null {
  try {
    const userIdClaim = getJwtClaim(token, 'id');
    if (typeof userIdClaim === 'string' && userIdClaim !== '') {
      return userIdClaim;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Extract user type from JWT token payload.
 * Tries common claim keys: 'userType'.
 * @param token JWT token string
 * @returns User type string (single) or null if invalid ormissing
 */
export function getUserTypeFromToken(token: string | undefined): string | null {
  try {
    if (token === undefined) return null;
    const userType = getJwtClaim(token, 'userType');
    if (typeof userType === 'string' && userType !== '') {
      return userType;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Extract organization ID from JWT token payload.
 * Reads the authenticated session claim `organizationId`.
 * @param token JWT token string
 * @returns Organization ID string or null if invalid/missing
 */
export function getOrganizationIdFromToken(token: string | undefined): string | null {
  try {
    if (token === undefined) return null;
    const organizationId = getJwtClaim(token, 'organizationId');
    if (typeof organizationId === 'string' && organizationId !== '') {
      return organizationId;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * True when the token belongs to the organization whose phone verification
 * is bypassed with the fixed auto-verification code.
 */
export function isAutoPhoneVerificationOrganizationToken(token: string | undefined): boolean {
  return getOrganizationIdFromToken(token) === AUTO_PHONE_VERIFICATION_ORGANIZATION_ID;
}
